import { defineStore, acceptHMRUpdate } from "pinia";

export type NotificationType = "info" | "warning" | "error" | "success";

export interface INotification {
  id: string;
  message: string;
  type: NotificationType;
}

export interface INotificationState {
  notifications: INotification[];
}

export interface INotificationActions {
  addNotification(message: string, type: NotificationType): void;
  removeNotification(id: string): void;
  notifyInfo(message: string): void;
  notifySuccess(message: string): void;
  notifyWarning(message: string): void;
  notifyError(message: string): void;
}

const NOTIFICATION_DELAY = 5000;

export const useNotificationStore = defineStore<
  "notification",
  INotificationState,
  {},
  INotificationActions
>("notification", {
  state: () => ({
    notifications: [],
  }),

  actions: {
    /**
     * Add notification to the store
     * @param {string} message - Message of the notification
     * @param {NotificationType} type - Type of the notification
     */
    addNotification(message: string, type: NotificationType) {
      const notification = { id: Date.now().toString(), message, type };
      this.$patch({ notifications: [notification, ...this.notifications] });
      setTimeout(() => {
        this.removeNotification(notification.id);
      }, NOTIFICATION_DELAY);
    },

    /**
     * Remove notification by id
     * @param {string} id - Id of the notification to be removed
     */
    removeNotification(id: string) {
      this.$patch({
        notifications: this.notifications.filter((n) => n.id !== id),
      });
    },

    /**
     * Notify an info
     * @param {string} message - Message of the notification
     */
    notifyInfo(message: string) {
      this.addNotification(message, "info");
    },

    /**
     * Notify a success
     * @param {string} message - Message of the notification
     */
    notifySuccess(message: string) {
      this.addNotification(message, "success");
    },

    /**
     * Notify a warning
     * @param {string} message - Message of the notification
     */
    notifyWarning(message: string) {
      this.addNotification(message, "warning");
    },

    /**
     * Notify an error
     * @param {string} message - Message of the notification
     */
    notifyError(message: string) {
      this.addNotification(message, "error");
    },
  },
});
